'use client';

import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';

import { zodResolver } from '@hookform/resolvers/zod';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';

import { logger } from '@packages/shared/src/utils/logger/logger';
import { useLocalStorage } from '@packages/shared/src/hooks/useLocalStorage/useLocalStorage';
import type { Product } from '@packages/gql/generated/shopping/graphql';
import { AddReviewDataDocument } from '@packages/gql/generated/shopping/AddReviewDataDocument';
import { StoreRatingDocument } from '@packages/gql/generated/shopping/StoreRatingDocument';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';

import type { CustomerReviewsSource } from '../../types';
import { AddReviewForm } from '../AddReviewForm/AddReviewForm.client';
import { AddReviewPreview } from '../AddReviewPreview/AddReviewPreview.client';
import { createSubmitData } from './createSubmitData/createSubmitData';
import { CompactProduct } from '../../../CompactProduct/CompactProduct.client';
import type { CreateReviewFormSchema } from '../AddReviewFormValidation/AddReviewFormValidation';
import { createReviewFormSchema } from '../AddReviewFormValidation/AddReviewFormValidation';
import { MultiStepFormSkeleton } from './MultiStepFormSkeleton/MultiStepFormSkeleton';
import { useCustomerReviewsTracking } from '../../Tracking/useCustomerReviewsTracking/useCustomerReviewsTracking';
import { multiStepFormMessages } from './MultiStepForm.messages';

export const localStorageKey = 'customer-review-form';

export type SavedFormState = {
  currentArticleNumber: string;
  formValues: Record<string, unknown>;
};

/* GraphQL */ `
  query AddReviewData($locale: String!, $sku: String!, $akl: String!) {
    product(id: $sku, fallbackId: $akl, locale: $locale) {
      orderNumber
      promotion
      availability {
        state
      }
      name
      price {
        ...ProductPriceFragment
      }
      delivery {
        deliverySize
      }
      media(limit: 1) {
        url
        type
      }
    }
  }
`;

/* GraphQL */ `
  mutation StoreRating($rating: NewRatingInputInput!) {
    storeRating(rating: $rating) {
      errors {
        cause
        key
      }
      success
    }
  }
`;

export type MultiStepFormProps = Pick<Product, 'sku' | 'akl'> & {
  /**
   * On submit success callback
   */
  onSuccess: () => void;
  /**
   * On submit error callback
   */
  onError: () => void;
  source?: CustomerReviewsSource;
};

/**
 * Multi step form component with stepper logic and form validation
 */
const MultiStepForm = ({ sku, akl, onSuccess, onError, source }: MultiStepFormProps) => {
  const { formatMessage } = useIntl();
  const { locale } = useI18n();

  const [savedFormState, setSavedFormState, removeSavedFormState] =
    useLocalStorage<SavedFormState | null>({
      key: localStorageKey,
      defaultValue: null,
    });

  const {
    trackCustomerReviewFormDisplayed,
    trackCustomerReviewFormSubmitted,
    trackCustomerReviewButtonClick,
  } = useCustomerReviewsTracking();

  const [{ data, error, fetching }] = useQuery({
    query: AddReviewDataDocument,
    variables: { sku, akl, locale },
  });
  const [, storeRating] = useAuthMutation(StoreRatingDocument);

  const methods = useForm<CreateReviewFormSchema>({
    resolver: zodResolver(createReviewFormSchema),
    mode: 'all',
    defaultValues: {
      productId: akl,
      recommended: null,
      title: '',
      name: '',
      description: '',
    },
  });

  const { reset } = methods;

  const displayFormTracked = React.useRef(false);
  const formResetByLocalStorage = React.useRef(false);

  const [step, setStep] = React.useState<'formStep' | 'previewStep'>('formStep');

  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  // Restore form state from LocalStorage if user has already filled the form and akl is the same as in saved form state
  useEffect(() => {
    if (
      !formResetByLocalStorage.current &&
      savedFormState &&
      savedFormState.currentArticleNumber === akl
    ) {
      reset(savedFormState.formValues);
      formResetByLocalStorage.current = true;
    }
  }, [akl, reset, savedFormState]);

  useEffect(() => {
    // this is needed to scroll to the top of the form when user switches between preview and form step
    if (wrapperRef.current) {
      wrapperRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [step, wrapperRef]);

  if (fetching) return <MultiStepFormSkeleton />;
  if (error || !data?.product) return null;

  if (!displayFormTracked.current) {
    trackCustomerReviewFormDisplayed();
    displayFormTracked.current = true;
  }

  const saveFormState = () => {
    const formValues = methods.getValues();
    setSavedFormState({
      currentArticleNumber: akl,
      formValues,
    });
  };

  const clearFormState = () => {
    removeSavedFormState();
    reset();
  };

  const { orderNumber, promotion, availability, media, name, price, delivery } = data.product;
  const {
    formState: { isValid, isSubmitting },
    trigger,
    handleSubmit,
    getValues,
  } = methods;

  const handleStep = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (step === 'formStep') {
      const valid = await trigger();
      if (valid) {
        setStep('previewStep');
        saveFormState();
        trackCustomerReviewButtonClick('Weiter zur Vorschau');
      }
    } else {
      trackCustomerReviewButtonClick('Bewertung bearbeiten');
      setStep('formStep');
    }
  };

  const onValidSubmitHandler: SubmitHandler<CreateReviewFormSchema> = async (values) => {
    const { data: storeRatingData, error: storeRatingError } = await storeRating({
      rating: {
        ...createSubmitData(values, locale),
        ...(source === 'MyAccount' && { variationId: sku }),
      },
    });

    if (storeRatingData?.storeRating?.success) {
      onSuccess();
      trackCustomerReviewButtonClick(`${getValues('rating').toString()} Sterne`);
      trackCustomerReviewFormSubmitted(
        getValues(),
        orderNumber + promotion,
        // This is until now the easiest way to check, if article is fully selected or not.
        // If this is not working properly, please implement dimensions.
        availability.state === 'UNSELECTED' ? undefined : sku,
      );
      clearFormState();
    }

    if (storeRatingError || !storeRatingData?.storeRating?.success) {
      logger.error(storeRatingError, 'Error submitting rating');
      onError();
    }
  };

  return (
    <div className="divide-color-grey scroll-m-2 divide-y" ref={wrapperRef}>
      <div className="py-1.5">
        <CompactProduct
          media={media}
          name={name}
          price={price}
          delivery={delivery}
          withProductName
          showTaxHint={false}
        />
      </div>
      <div className="pt-2">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onValidSubmitHandler)} onBlur={saveFormState}>
            {step === 'formStep' ? <AddReviewForm /> : <AddReviewPreview />}
            <div className="mt-2 flex flex-col justify-end gap-2 md:flex-row">
              {step === 'formStep' ? (
                <Button
                  type="button"
                  size="large"
                  color="primary"
                  onClick={handleStep}
                  disabled={!isValid}
                >
                  {formatMessage(multiStepFormMessages.buttonPreview)}
                </Button>
              ) : (
                <>
                  <Button onClick={handleStep} size="large" disabled={isSubmitting}>
                    {formatMessage(multiStepFormMessages.buttonEdit)}
                    <span className="icon-emp-edit" />
                  </Button>
                  <Button
                    type="submit"
                    size="large"
                    color="primary"
                    disabled={!isValid || isSubmitting}
                  >
                    {formatMessage(multiStepFormMessages.buttonSubmit)}
                    <span className="icon-emp-checkmark" />
                  </Button>
                </>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

// must be a default export because of lazy loading
// eslint-disable-next-line import-x/no-default-export
export default MultiStepForm;
